import React, { useEffect, useState } from 'react'
import { scroller } from 'react-scroll'
import {
  Container,
  TextBody,
  TextCaption,
  FieldText,
  FieldHidden,
  Icon,
  ListContainer,
  RadioGroup,
  SelectAutocomplete,
  ToastContext
} from '@pmi/pmi-iqos-ds-react'
import {
  registerParam,
  unegisterParam,
  validateParam,
  validateForm,
  constants,
  htmlTranslation,
  getToastMessagesFromError,
  getBase64FromImg,
  getToastMessagesFromResponse
} from '@pmi/pmi-iqos-shared-react'
import { DcxApiManager, ZynAnalytics } from '@zyn-common/services'
import CheckoutTVA from './checkout-tva'

const CheckoutShipping = ({
  translations,
  toggleNewShippingAddress,
  setToggleNewShippingAddress,
  newShippingAddress,
  isDefaultAddressValid,
  handleChangeShippingAddressType,
  shippingAddress,
  validateAddress,
  setFormIsValidTVA,
  setValidateAddress,
  line,
  isSagawa,
  getSavedNewShippingAddress,
  setDefaultShippingAddress,
  zipCodeErrorMessage,
  isMonitorPack,
  loading,
  handleChangeInputsNewShippingAddress,
  formId,
  handleChangeZipCode,
  setZenrinErrors,
  zenrinErrors,
  updateCheckoutDeliveryOptions,
  setLoadingStep1,
  setNewShippingAddressIsValid
}) => {
  const [postalCodeErrors, setPostalCodeErrors] = useState([])
  const [cityErrors, setCityErrors] = useState([])
  const [stateErrors, setStateErrors] = useState([])
  const [addressLineErrors, setAddressLineErrors] = useState([])

  const [savingTvaData, setSavingTvaData] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [fileNameFront, setFileNameFront] = useState('')
  const [fileNameBack, setFileNameBack] = useState('')
  const [fileKeyFront, setFileKeyFront] = useState('')
  const [fileKeyBack, setFileKeyBack] = useState('')
  const [tvaData] = useState({
    address: '',
    city: '',
    postal_code: '',
    prefecture: ''
  })
  const [updatedTvaData, setUpdatedTvaData] = useState(tvaData)

  const toastContext = ToastContext.getContextActions()
  const popupToast = toastContext.popupToast
  const deliveryAddressFormSection = 'delivery-address-form-section'

  const api = new DcxApiManager(line)

  useEffect(() => {
    if (newShippingAddress.city !== '') {
      const errors = handleChangeInput('city', newShippingAddress.city)
      setCityErrors(errors)
    }
  }, [newShippingAddress])

  useEffect(() => {
    if (validateAddress) {
      handleValidateAddress()
    }
  }, [validateAddress])

  const formFieldNames = {
    postalCode: 'postalCode',
    city: 'city',
    state: 'state',
    addressLine1: 'addressLine1'
  }

  const validators = {}

  const updateValidators = () => {
    validators[formFieldNames.postalCode] = registerParam(
      [
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.postalCode],
          description: translations.tk_zenrinValidation
        }
      ],
      false,
      formId,
      newShippingAddress.postalCode,
      errors => {
        setPostalCodeErrors(errors)
      }
    )
    validators[formFieldNames.city] = registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_checkoutAddressCharactersValidation
        },
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.city],
          description: translations.tk_zenrinValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_cityAddressInvalidComma
        }
      ],
      false,
      formId,
      newShippingAddress.city,
      errors => {
        setCityErrors(errors)
      }
    )
    validators[formFieldNames.state] = registerParam(
      [
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.prefecture],
          description: translations.tk_zenrinValidation
        }
      ],
      false,
      formId,
      newShippingAddress.state,
      errors => {
        setStateErrors(errors)
      }
    )
    validators[formFieldNames.addressLine1] = registerParam(
      [
        {
          type: 'addressCharactersValidation',
          rule: constants.ADDRESS_REGEX_VALIDATION,
          description: translations.tk_checkoutAddressCharactersValidation
        },
        {
          type: 'zenrinValidation',
          rule: zenrinErrors && zenrinErrors[formFieldNames.addressLine1],
          description: translations.tk_zenrinValidation
        },
        {
          type: 'length',
          rule: 'max_45',
          description: translations.tk_characterExceeded
        },
        {
          type: 'contains',
          rule: ',',
          description: translations.tk_buildingNameInvalidComma
        }
      ],
      true,
      formId,
      newShippingAddress.addressLine1,
      errors => {
        setAddressLineErrors(errors)
      }
    )
  }

  updateValidators()

  useEffect(() => {
    return () => {
      Object.keys(formFieldNames).forEach(fieldNameKey =>
        unegisterParam(validators[formFieldNames[fieldNameKey]])
      )
    }
  }, [])

  useEffect(() => {
    if (zenrinErrors && toggleNewShippingAddress) {
      updateValidators()
      setNewShippingAddressIsValid(validateForm(formId))
    }
  }, [zenrinErrors])

  const handleChangeCity = e => {
    handleChangeInputsNewShippingAddress(e)
    const errors = handleChangeInput(e.target.name, e.target.value)
    setCityErrors(errors)
  }

  const handleChangeAddressLine = e => {
    handleChangeInputsNewShippingAddress(e)
    const errors = handleChangeInput(e.target.name, e.target.value)
    setAddressLineErrors(errors)
  }

  const handleChangeInput = (name, value) => {
    const errors = validateParam(value, validators[name], formId)
    return errors
  }

  const callAddressVerifiedEvent = () => {
    window.dispatchEvent(new window.CustomEvent('addressVerified'))
  }

  const formattedZipCode = postalCode => {
    return '〒' + postalCode.substring(0, 3) + '-' + postalCode.substring(3, 7)
  }

  const handleChangeZipCodeTVA = selectedOption => {
    if (selectedOption && selectedOption.item) {
      setUpdatedTvaData({
        ...updatedTvaData,
        postal_code: selectedOption.value,
        city: selectedOption.item.city + ' ' + selectedOption.item.village,
        prefecture: selectedOption.item.state
      })
    }
  }

  const formattedShippingAddress = address => {
    return (
      address &&
      `${formattedZipCode(address.postalCode)} ${address.state} ${
        address.city
      } ${address.addressLine1}`
    )
  }

  const promiseOptions = zipcode =>
    zipcode && zipcode.length > 2
      ? api.auth
          .getAddressInfo({
            params: {
              ...(isSagawa && { s: true }),
              zipcode
            }
          })
          .then(res => {
            const results = res.zip_codes.map(item => {
              return {
                value: item.zip_code,
                label:
                  item.zip_code +
                  ' ' +
                  item.state +
                  ' ' +
                  item.city +
                  ' ' +
                  item.village,
                item: item
              }
            })
            return results
          })
      : Promise.resolve([])

  const handleChangeInputTVA = e => {
    const name = e.target.name
    const value = e.target.value
    switch (name) {
      case 'address':
        setUpdatedTvaData({ ...updatedTvaData, address: value })
        break
      case 'city':
        setUpdatedTvaData({ ...updatedTvaData, city: value })
        break
      case 'prefecture':
        setUpdatedTvaData({ ...updatedTvaData, prefecture: value })
        break
      default:
    }
  }

  const uploadPhoto = async (file, type) => {
    // Only images and less than 6 Megabyte
    if (
      (file && file.type && !file.type.match(/image-*/)) ||
      (file && file.size >= 6000000)
    ) {
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_unableToUpdateDelivery
      })
      return
    }

    setUploadingFile(true)

    const uploadCb = convertedImg => {
      const fileKey = type === 'front' ? 'fileKeyFront' : 'fileKeyBack'
      const fileName = type === 'front' ? 'fileNameFront' : 'fileNameBack'

      api.profile
        .uploadTVAImage({ base64image: convertedImg.split(',')[1] })
        .then(res => {
          if (fileKey === 'fileKeyFront') {
            setFileKeyFront(res.imageKey)
          }
          if (fileKey === 'fileKeyBack') {
            setFileKeyBack(res.imageKey)
          }
          if (fileName === 'fileNameFront') {
            setFileNameFront(file.name)
          }
          if (fileName === 'fileNameBack') {
            setFileNameBack(file.name)
          }
          setUploadingFile(false)
          ZynAnalytics.checkoutTvaClickUpload(true, type)
        })
        .catch(err => {
          setUploadingFile(false)
          popupToast({
            type: ToastContext.types.error,
            title: getToastMessagesFromError(
              err,
              translations.tk_unableToUpdateDelivery
            )
          })
        })
    }

    await getBase64FromImg(file, uploadCb, () => {
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_unableToUpdateDelivery
      })
    })
  }

  const scrollToContainer = () => {
    scroller.scrollTo(deliveryAddressFormSection, {
      duration: 500,
      smooth: true
    })
  }

  const handleValidateAddress = async () => {
    if (!fileKeyFront || !fileKeyBack) {
      popupToast({
        type: ToastContext.types.error,
        title: fileKeyFront
          ? translations.tk_backImageMissing
          : translations.tk_frontImageMissing
      })
      setValidateAddress(false)
    } else {
      setSavingTvaData(true)
      const newTvaData = {
        image_front: fileKeyFront,
        image_back: fileKeyBack,
        postal_code: updatedTvaData.postal_code,
        prefecture: updatedTvaData.prefecture,
        city: updatedTvaData.city,
        address: updatedTvaData.address
      }

      try {
        setLoadingStep1(true)
        const res = await api.profile.updateTobaccoDelivery(newTvaData, true)
        const data = {
          addressLine1: updatedTvaData.address,
          city: updatedTvaData.prefecture,
          state: updatedTvaData.city,
          postalCode: updatedTvaData.postal_code
        }
        getSavedNewShippingAddress(data, false)
        setDefaultShippingAddress(data)

        setToggleNewShippingAddress(false)
        setUpdatedTvaData(tvaData)
        updateCheckoutDeliveryOptions(updatedTvaData.postal_code)
        setFileKeyFront('')
        setFileNameFront('')
        setFileKeyBack('')
        setFileNameBack('')

        popupToast({
          type: ToastContext.types.success,
          title: getToastMessagesFromResponse(
            res,
            translations.tk_deliveryUpdated,
            translations.tk_unableToUpdateDelivery
          )
        })
        callAddressVerifiedEvent()
        // enableNextStep(0)
      } catch (err) {
        // Zenrin validations for CANS Flow - TVA
        const newZenrinErrors = err.data.rejectedFields.reduce(
          (erros, field) => ({ ...erros, [field]: updatedTvaData[field] }),
          {}
        )
        if (err.data.rejectedFields.includes('address')) {
          popupToast({
            type: ToastContext.types.error,
            subtitle: translations.tk_zenrinRejectedAddress
          })
        }
        if (err.data.rejectedFields.includes('city')) {
          popupToast({
            type: ToastContext.types.error,
            subtitle: translations.tk_zenrinRejectedCity
          })
        }
        setZenrinErrors(newZenrinErrors)
        scrollToContainer()
      } finally {
        setSavingTvaData(false)
        setValidateAddress(false)
        setLoadingStep1(false)
      }
    }
  }

  const renderShippingAddressSelection = () => {
    // Block with address pre-filled
    const addressTypes = [
      {
        value: 0,
        content: (
          <Container>
            <TextBody
              size={TextBody.sizes.s}
              {...htmlTranslation(
                translations,
                'tk_checkoutDefaultAddressRadio'
              )}
            />
            <TextCaption size={TextCaption.sizes.s}>
              {formattedShippingAddress(shippingAddress)}
            </TextCaption>
          </Container>
        )
      },
      {
        value: 1,
        content: (
          <Container
            fullHeight
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.between}
            alignItems={Container.alignItemsValues.center}>
            <Container>
              <Container
                display={[Container.displays.block, Container.displays.none]}>
                <TextBody
                  size={TextBody.sizes.s}
                  {...htmlTranslation(
                    translations,
                    'tk_checkoutEnterAddressRadio'
                  )}
                />
              </Container>
              <Container
                display={[Container.displays.none, Container.displays.block]}>
                <TextCaption size={TextCaption.sizes.s}>
                  {translations.tk_checkoutEnterAddressRadio}
                </TextCaption>
              </Container>
            </Container>
            <Container
              display={[Container.displays.none, Container.displays.block]}>
              <Icon size={Icon.sizes.xs} image={Icon.images.address} />
            </Container>
          </Container>
        )
      }
    ]
    return (
      <Container pt="4" pb="8" px={['0', '8']}>
        <Container px={['8', '0']}>
          <RadioGroup
            alternative
            name="shippingAddressType"
            label=""
            style={[RadioGroup.styles.default, RadioGroup.styles.square]}
            columns={[1, 2]}
            items={addressTypes}
            getItemValue={(item, index) => addressTypes[index].value}
            getItemContent={(item, index) => addressTypes[index].content}
            isItemSelected={(item, index) =>
              addressTypes[index].value === 0
                ? !toggleNewShippingAddress
                : toggleNewShippingAddress
            }
            onChangeHandler={handleChangeShippingAddressType}
            data-testing="Checkout_RadioGroup_ShippingAddressType"
          />
        </Container>
      </Container>
    )
  }

  const renderNewShippingAddressForm = () => {
    // Block to click to add new address (TVA)
    return isMonitorPack ? (
      <Container px={['0', '8']} pb={['10', '8']}>
        <ListContainer
          columns={[1, 2]}
          xBetween="8"
          yBetween="5"
          overflow="visible">
          <SelectAutocomplete
            name={formFieldNames.postalCode}
            label={translations.tk_fieldName_zipCode}
            placeholder={translations.tk_fieldPlaceholder_zipCode}
            value={newShippingAddress.postalCode}
            onChangeHandler={handleChangeZipCode}
            loadOptions={promiseOptions}
            errors={zipCodeErrorMessage || postalCodeErrors}
            disabled={loading}
            data-testing="Checkout_SelectAutocomplete_ZipCode"
          />
          <FieldText
            name={formFieldNames.state}
            label={translations.tk_fieldName_state}
            value={newShippingAddress.state}
            onChangeHandler={handleChangeInputsNewShippingAddress}
            errors={stateErrors}
            disabled
            data-testing="Checkout_FieldText_State"
          />
          <FieldText
            name={formFieldNames.city}
            label={translations.tk_fieldName_city}
            value={newShippingAddress.city}
            onChangeHandler={handleChangeCity}
            errors={cityErrors}
            disabled={loading}
            data-testing="Checkout_FieldText_City"
          />
          <FieldText
            name={formFieldNames.addressLine1}
            label={translations.tk_fieldName_address}
            value={newShippingAddress.addressLine1}
            onChangeHandler={handleChangeAddressLine}
            errors={addressLineErrors}
            disabled={loading}
            data-testing="Checkout_FieldText_AddressLine1"
          />
        </ListContainer>
        <FieldHidden
          value={newShippingAddress.postalCodeHidden}
          name="postalCodeHidden"
        />
      </Container>
    ) : (
      <CheckoutTVA
        savingTvaData={savingTvaData}
        translations={translations}
        uploadPhoto={uploadPhoto}
        fileNameFront={fileNameFront}
        fileNameBack={fileNameBack}
        updatedTvaData={updatedTvaData}
        handleChangeInput={handleChangeInputTVA}
        handleChangeZipCode={handleChangeZipCodeTVA}
        uploadingFile={uploadingFile}
        line={line}
        setFormIsValidTVA={setFormIsValidTVA}
        zenrinErrors={zenrinErrors}
        deliveryAddressFormSection={deliveryAddressFormSection}
      />
    )
  }

  return (
    <Container>
      {/* {hasTobacco ? (
        renderShippingAddressWithTobacco()
      ) : ( */}
      <Container
        pt={
          !isDefaultAddressValid && toggleNewShippingAddress ? ['10', '6'] : '0'
        }>
        {isDefaultAddressValid && renderShippingAddressSelection()}
        {toggleNewShippingAddress && renderNewShippingAddressForm()}
      </Container>
    </Container>
  )
}

export default CheckoutShipping
